/**
 * @ipc/admin-ui · widgets/app-shell · nav-sidebar — навбар-остров.
 * Desktop: карточка 244px (sticky). Mobile/TWA: тот же контент в Sheet (см. mobile-nav).
 * Пункты меню — routeManifest + RBAC (can).
 */
import { NavLink } from 'react-router-dom';
import { cn, can } from '@/shared/lib';
import type { SessionUser } from '@/entities/session';
import { routeManifest } from '@/app/routes/route-manifest';
import { BrandLogo } from './brand-logo';
import { ProfileMenu } from './profile-menu';

export interface NavSidebarContentProps {
  user: SessionUser;
  /** Закрыть drawer после перехода (mobile Sheet). */
  onNavigate?: () => void;
  className?: string;
  /** Скрыть логотип сверху (если уже есть в mobile header). */
  hideBrand?: boolean;
}

/** Список пунктов + профиль. Общий для desktop-острова и mobile Sheet. */
export function NavSidebarContent({
  user,
  onNavigate,
  className,
  hideBrand = false,
}: NavSidebarContentProps) {
  const items = routeManifest.filter(
    (entry) => entry.nav !== undefined && (entry.permission === null || can(user.permissions, entry.permission)),
  );

  return (
    <nav
      className={cn(
        'flex h-full min-h-0 w-full flex-col bg-sidebar p-3 text-sidebar-foreground',
        className,
      )}
    >
      {hideBrand ? null : (
        <div className="flex items-center gap-2 px-2 pt-1 pb-4">
          <BrandLogo className="block h-[17px]" />
        </div>
      )}

      <div className="-mx-2 flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2">
        {items.map((entry) => {
          const Icon = entry.nav?.icon;
          return (
            <NavLink
              key={entry.path}
              to={entry.path}
              end={entry.index === true}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium whitespace-nowrap',
                  isActive
                    ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                    : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                )
              }
            >
              {Icon ? <Icon className="size-[18px] shrink-0 opacity-85" /> : null}
              <span>{entry.nav?.label}</span>
            </NavLink>
          );
        })}
      </div>

      <div className="mt-3 border-t border-sidebar-border pt-3">
        <ProfileMenu user={user} />
      </div>
    </nav>
  );
}

/** Desktop island: sticky 244px. На mobile скрывается через AppShell. */
export function NavSidebar({ user }: { user: SessionUser }) {
  return (
    <NavSidebarContent
      user={user}
      className="sticky top-3 h-[calc(100vh-1.5rem)] w-[244px] shrink-0 rounded-xl border border-sidebar-border"
    />
  );
}
